import { Asset, DenormalizedCustomizableQuestion, QuestionInputType } from '@packages/types'
import React from 'react'

import useQuestionProps from 'themes/booklike/questionPanel/hooks/useQuestionProps'

import CheckboxQuestion from './CheckboxQuestion'
import ColorPickerQuestion from './ColorPickerQuestion'
import DropdownQuestion from './DropdownQuestion'
import FileUploadQuestion from './FileUploadQuestion'
import LabelQuestion from './LabelQuestion'
import RadioQuestion from './RadioQuestion'
import TextQuestion from './TextQuestion'
import ThumbnailQuestion from './ThumbnailQuestion'

import './Question.scss'
import './MultiAnswerSelectionsMessage.scss'

interface QuestionInputFactoryProps {
  question: DenormalizedCustomizableQuestion
  pricing: Record<string, string>
  currency: string
  lang: string
  country: string
  requiredWarning?: boolean
  selectAnswer: (answerId?: string) => void
  themeConfig: { showLabel: boolean }
  answersOutOfStock: string[]
  className?: string
  children?: React.ReactNode
  selectAnswers: (answerIds: string[]) => void
  createColorAnswer: (questionId: string, color: string) => void
  updateColorAnswer: (questionId: string, answerId: string, color: string) => void
  removeAnswer: (answerId: string) => void
  createTextAnswer: (questionId: string, text: string) => void
  createLogoAnswer: (questionId: string, logo: Asset) => void
  createAssetAnswer: (questionId: string, value: Asset) => void
  updateTextAnswer: (questionId: string, answerId: string, text: string) => void
  focusQuestion: () => void
}

const QuestionInputFactory = (props: QuestionInputFactoryProps) => {
  const { question } = props

  switch (question.inputType) {
    case QuestionInputType.Thumbnail:
      return <ThumbnailQuestion {...props} />
    case QuestionInputType.LargeThumbnail:
      return <ThumbnailQuestion isLarge {...props} />
    case QuestionInputType.SmallRadio:
      return <RadioQuestion {...props} />
    case QuestionInputType.Dropdown:
      return <DropdownQuestion {...props} />
    case QuestionInputType.Text:
      return <TextQuestion {...props} />
    case QuestionInputType.Checkbox:
      return <CheckboxQuestion {...props} />
    case QuestionInputType.File:
      return <FileUploadQuestion {...props} />
    case QuestionInputType.Label:
      return <LabelQuestion {...props} />
    case QuestionInputType.ColorPicker:
      return <ColorPickerQuestion {...props} />
    default:
      return null
  }
}

const Question = ({ question, ...rest }: { question: DenormalizedCustomizableQuestion }) => {
  const questionProps = useQuestionProps(question)

  return (
    <div className="question">
      <QuestionInputFactory
        question={question}
        pricing={questionProps.pricing}
        currency={questionProps.currency}
        lang={questionProps.lang}
        country={questionProps.country}
        selectAnswer={questionProps.handleSelectAnswer}
        selectAnswers={questionProps.handleSelectAnswers}
        removeAnswer={questionProps.handleRemoveAnswer}
        createTextAnswer={questionProps.handleCreateTextAnswer}
        createLogoAnswer={questionProps.handleCreateLogoAnswer}
        createAssetAnswer={questionProps.handleCreateAssetAnswer}
        createColorAnswer={questionProps.handleCreateColorAnswer}
        updateTextAnswer={questionProps.handleUpdateTextAnswer}
        updateColorAnswer={questionProps.handleUpdateColorAnswer}
        focusQuestion={questionProps.handleFocusQuestion}
        themeConfig={questionProps.themeConfig}
        answersOutOfStock={questionProps.answersOutOfStock}
        {...rest}
      />
    </div>
  )
}

export default Question
